import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, finalize, startWith, switchMap, take, takeUntil, tap } from 'rxjs';

import { ChecklistTemplatesApiService } from 'shared/services/api/checklist-templates.api.service';
import { DialogService } from 'shared/services/dialog.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { ChecklistTemplate } from 'shared/models/checklist-template';
import { APIResponse } from 'shared/models/api-response';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { SettingsService } from 'shared/services/settings.service';

@Component({
  selector: 'app-checklist-library-dialog',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, MatCheckboxModule
  ],
  templateUrl: './checklist-library-dialog.component.html',
  styleUrl: './checklist-library-dialog.component.scss'
})
export class ChecklistLibraryDialogComponent implements OnInit, OnDestroy {

  mode: 'SELECT' | 'BROWSE';
  disabledList:string[] = []
  isLoading = false;
  isProcessing = false;
  hasCreateChecklist = false;
  hasDeleteChecklist = false;
  
  templates: ChecklistTemplate[] = [];
  searchCtrl = new FormControl('');
  quickAddCtrl = new FormControl('', [Validators.required]);
  
  selection = new Map<string, ChecklistTemplate>();
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { mode: 'SELECT' | 'BROWSE', disabledList?: string[] },
    private _dialogRef: MatDialogRef<ChecklistLibraryDialogComponent>,
    private _fb: FormBuilder,
    private _templatesApi: ChecklistTemplatesApiService,
    private _dialogService: DialogService,
    private _snackbar: SnackbarService,
    private _settingsService: SettingsService
  ) {
    this.mode = data.mode || 'BROWSE';
    this.disabledList = data.disabledList || []
   
  }

  ngOnInit(): void {
    this._settingsService.getUserSettings().pipe(
      takeUntil(this.destroy$)
    ).subscribe(userSettings => {
      if (userSettings?.user) {
        const isOwner = userSettings.user.role === 'user';
        this.hasCreateChecklist = isOwner || !!userSettings.user.permissions?.includes('CREATE_CHECKLIST');
        this.hasDeleteChecklist = isOwner || !!userSettings.user.permissions?.includes('DELETE_CHECKLIST');
      }
    });

    this.searchCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.isLoading = true),
      switchMap(query => this._templatesApi.get({ searchQuery: query || '' }).pipe(
        finalize(() => this.isLoading = false)
      )),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: APIResponse) => {
        this.templates = res.success ? (res.data as ChecklistTemplate[]) : [];
      }
    });
  }

  isDisabled(template: ChecklistTemplate): boolean {
    return this.disabledList.includes(template.name);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  quickAddItem(): void {
    if (this.quickAddCtrl.invalid) return;
    this.isProcessing = true;
    const payload = { name: this.quickAddCtrl.value };
    this._templatesApi.create(payload).pipe(
      take(1),
      finalize(() => this.isProcessing = false)
    ).subscribe({
      next: (res: APIResponse) => {
        if (res.success) {
          this.templates.unshift(res.data);
          this.quickAddCtrl.reset();
          this._snackbar.showSnackBar('Item added to library.', 'success');
        } else {
          this._snackbar.showSnackBar('Failed to add item.', 'error');
        }
      }
    });
  }

  deleteItem(template: ChecklistTemplate, event: MouseEvent): void {
    event.stopPropagation();
    this._dialogService.confirmDialog({
      title: 'Delete Item',
      message: `Are you sure you want to delete "${template.name}" from the library?`,
      type: 'danger',
      confirmText: 'Delete'
    })
    .subscribe(confirmed => {
      if (confirmed === true) {
        this._templatesApi.delete(template._id)
        .pipe(take(1)).subscribe(res => {
          this.templates = this.templates.filter(t => t._id !== template._id);
          this.selection.delete(template._id);
          this._snackbar.showSnackBar('Item deleted.', 'success');
        });
      }
    });
  }

toggleSelection(template: ChecklistTemplate): void {
    if (this.mode === 'BROWSE' || this.isDisabled(template)) return;
    if (this.selection.has(template._id)) {
      this.selection.delete(template._id);
    } else {
      this.selection.set(template._id, template);
    }
  }

  toggleSelectAll(event: any): void {
    if (event.checked) {
      this.templates.forEach(t => {
        if (!this.isDisabled(t)) {
          this.selection.set(t._id, t);
        }
      });
    } else {
      this.selection.clear();
    }
  }

  isAllSelected(): boolean {
    return this.templates.length > 0 && this.selection.size === this.templates.length;
  }

  addSelectedItems(): void {
    this._dialogRef.close(Array.from(this.selection.values()));
  }
}